import { Link } from "react-router-dom";
import { useState } from "react";
import { toast } from "sonner";
import { HeartPulse, UserPlus, Search, PhoneCall, ShieldCheck, Lock, Loader2, Check, Stethoscope } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import api, { apiErr } from "../lib/api";
import { HealthSearch } from "../components/HealthSearch";

const SAMPLE_PROFESSIONS = [
  "Nursing", "PSW / Caregiving", "Pharmacy", "Lab", "Imaging", "Therapy",
  "Mental Health", "Dental", "Vision", "Paramedics", "Surgical Support", "Home Care",
];

const WORKER_STEPS = [
  { icon: UserPlus, title: "Build your clinical profile", desc: "Pick your profession, add your license, specialty and clinical experience — free to list." },
  { icon: ShieldCheck, title: "Get verified", desc: "Add credentials so employers trust and prioritise your profile." },
  { icon: PhoneCall, title: "Get contacted directly", desc: "Facilities find you by match score and call you — no agency middleman." },
];
const EMPLOYER_STEPS = [
  { icon: Search, title: "Filter by profession", desc: "Nursing, PSW, Pharmacy, Lab, Imaging, Therapy and more — plus shift, wage & license." },
  { icon: Stethoscope, title: "See credentials up front", desc: "License type, specialty and clinical years, ranked by fit." },
  { icon: PhoneCall, title: "Call instantly", desc: "One tap to reach the right healthcare worker for your shift." },
];

export default function HealthMarketplace() {
  const { user } = useAuth();
  const isEmployer = user?.role === "employer";
  const entitled = user?.plan === "employer_pro";

  return (
    <div data-testid="marketplace-health">
      {/* HERO */}
      <section className="bg-[#06251E] text-white">
        <div className="max-w-6xl mx-auto px-5 py-14">
          <div className="font-mono2 text-xs uppercase tracking-[0.2em] text-[#5EEAD4] flex items-center gap-2">
            <HeartPulse size={15} /> Healthcare · shift · locum · per-diem
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-3">Darriker Health</h1>
          <p className="text-base text-[#B6D8CF] mt-4 max-w-2xl">On-demand healthcare talent — nurses, PSWs, pharmacy, lab, imaging, therapy, mental health & more. Verified credentials, direct contact, no agency markup.</p>
          {!user && (
            <div className="flex flex-wrap gap-3 mt-7">
              <Link to="/auth?mode=register" data-testid="health-cta-worker" className="bg-white text-[#06251E] px-6 py-3 text-sm font-medium hover:bg-[#B6D8CF] transition-colors">List your availability — free</Link>
              <Link to="/auth?mode=register" data-testid="health-cta-employer" className="border border-white px-6 py-3 text-sm font-medium hover:bg-white hover:text-[#06251E] transition-colors">Hire healthcare talent</Link>
            </div>
          )}
          <div className="flex flex-wrap gap-1.5 mt-7">
            {SAMPLE_PROFESSIONS.map((c) => (
              <span key={c} className="font-mono2 text-[10px] uppercase border border-white/25 text-[#B6D8CF] px-2.5 py-1">{c}</span>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-5 py-10">
        {isEmployer ? (
          entitled ? <HealthSearch /> : <HealthPaywall />
        ) : (
          <>
            <div className="grid md:grid-cols-2 gap-8">
              <StepColumn label="For healthcare workers" steps={WORKER_STEPS} />
              <StepColumn label="For employers & facilities" steps={EMPLOYER_STEPS} />
            </div>

            <div className="mt-10 border border-[#0A0A0A] p-8 text-center space-y-3">
              {user?.role === "flex" ? (
                <div data-testid="health-flex-cta" className="space-y-3">
                  <Stethoscope size={28} className="text-[#0E7C66] mx-auto" />
                  <h2 className="font-display font-bold text-xl">List yourself as a healthcare worker</h2>
                  <p className="text-sm text-[#4B5563] max-w-md mx-auto">On your profile, switch your sector to <b>Healthcare</b>, pick your profession and add your credentials to appear in Darriker Health searches.</p>
                  <Link to="/flex" data-testid="health-manage-listing" className="btn-ink inline-block px-6 py-2.5 text-sm font-medium">Manage my listing</Link>
                </div>
              ) : (
                <div className="space-y-3">
                  <h2 className="font-display font-bold text-xl">Hire verified healthcare talent, fast</h2>
                  <p className="text-sm text-[#4B5563]">Sign in as an employer to search and contact healthcare workers directly.</p>
                  <Link to="/auth" className="btn-ink inline-block px-6 py-2.5 text-sm font-medium">Sign in to browse</Link>
                </div>
              )}
            </div>

            <div className="mt-8 text-center">
              <Link to="/marketplace/onsite" className="font-mono2 text-xs text-[#0E7C66] hover:underline">Looking for general gig workers? Visit Darriker Flex →</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function HealthPaywall() {
  const [loading, setLoading] = useState(false);
  const subscribe = async () => {
    setLoading(true);
    try {
      const r = await api.post("/payments/checkout", { package_id: "employer_pro", origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(apiErr(e));
      setLoading(false);
    }
  };
  return (
    <div className="border-2 border-dashed border-[#0A0A0A] p-8" data-testid="health-paywall">
      <div className="flex items-center gap-2 mb-1">
        <Lock size={18} className="text-[#0E7C66]" />
        <h2 className="font-display font-bold text-xl">Darriker Health is part of Employer Pro</h2>
      </div>
      <p className="text-sm text-[#4B5563] mb-6 max-w-lg">Searching and contacting healthcare workers requires an Employer Pro subscription. It also unlocks unlimited job posts, the AI recruiter copilot and the full Flex + Remote marketplaces.</p>
      <div className="bg-white border border-[#0A0A0A] p-6 max-w-md">
        <div className="font-mono2 text-xs uppercase text-[#0E7C66]">Full recruiter suite</div>
        <div className="font-display font-black text-2xl tracking-tighter mt-1">Employer Pro</div>
        <div className="font-display font-black text-4xl tracking-tighter text-[#0E7C66] mt-2">$299<span className="text-base text-[#4B5563]">/mo</span></div>
        <ul className="mt-3 space-y-1 text-sm">
          <li className="flex gap-2"><Check size={15} className="text-[#0E7C66] shrink-0" />Search & contact healthcare workers</li>
          <li className="flex gap-2"><Check size={15} className="text-[#0E7C66] shrink-0" />Full Flex + Remote marketplaces</li>
          <li className="flex gap-2"><Check size={15} className="text-[#0E7C66] shrink-0" />Unlimited job posts + AI copilot</li>
          <li className="flex gap-2"><Check size={15} className="text-[#0E7C66] shrink-0" />Candidate shortlists & screening</li>
        </ul>
        <button data-testid="health-subscribe-btn" onClick={subscribe} disabled={loading} className="btn-ink w-full py-2.5 text-sm font-medium mt-4 inline-flex items-center justify-center gap-2 disabled:opacity-50">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <HeartPulse size={16} />} Get Employer Pro — $299/mo
        </button>
      </div>
    </div>
  );
}

function StepColumn({ label, steps }) {
  return (
    <div>
      <div className="font-mono2 text-xs uppercase tracking-wider text-[#0E7C66] mb-4">{label}</div>
      <div className="space-y-5">
        {steps.map((s, i) => {
          const SIcon = s.icon;
          return (
            <div key={i} className="flex gap-4">
              <div className="w-10 h-10 shrink-0 border border-[#0A0A0A] flex items-center justify-center">
                <SIcon size={18} className="text-[#0A0A0A]" />
              </div>
              <div>
                <div className="font-display font-bold text-base tracking-tight">{s.title}</div>
                <div className="text-sm text-[#4B5563]">{s.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
